import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { BillingProvider, useBilling } from './contexts/BillingContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import BillingBlocked from './pages/BillingBlocked';
import Navbar from './components/Layout/Navbar';
import AdminSidebar from './components/Admin/AdminSidebar';
import Footer from './components/Layout/Footer';
import MobileBottomNav from './components/Layout/MobileBottomNav';
import Home from './pages/Home';
import About from './pages/About';
import Immigration from './pages/Immigration';
import Blog from './pages/Blog';
import Contact from './pages/Contact';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import AdminDashboard from './pages/Admin/Dashboard';
import AdminPosts from './pages/Admin/Posts';
import AdminContacts from './pages/Admin/Contacts';
import AdminUsers from './pages/Admin/Users';
import AdminNewsletter from './pages/Admin/Newsletter';
import AdminNIF from './pages/Admin/NIF';
import AdminAdvogado from './pages/Admin/Advogado';
import AdminContrato from './pages/Admin/Contrato';
import AdminOrcamento from './pages/Admin/Orcamento';
import AdminServico from './pages/Admin/Servico';
import AdminEquipe from './pages/Admin/Equipe';
import PostForm from './pages/Admin/PostForm';
import AdminRedesSociais from './pages/Admin/RedesSociais';
import RedesSociaisForm from './pages/Admin/RedesSociaisForm';
import AdminLandingPages from './pages/Admin/LandingPages';
import LandingPageForm from './pages/Admin/LandingPageForm';
import AdminLandingQuizLeads from './pages/Admin/LandingQuizLeads';
import AdminAvatars from './pages/Admin/Avatars';
import AvatarForm from './pages/Admin/AvatarForm';
import AdminAgendamentos from './pages/Admin/Agendamentos';
import AgendamentoForm from './pages/Admin/AgendamentoForm';
import CRM from './pages/Admin/CRM';
import ClienteForm from './pages/Admin/ClienteForm';
import ClienteDetail from './pages/Admin/ClienteDetail';
import VendaForm from './pages/Admin/VendaForm';
import AdminSettings from './pages/Admin/Settings';
import AdminInvites from './pages/Admin/Invites';
import AdminVisaAnalysis from './pages/Admin/VisaAnalysis';
import VisaAnalysisForm from './pages/VisaAnalysis/VisaAnalysisForm';
import AdminResidenceAnalysis from './pages/Admin/ResidenceAnalysis';
import ResidenceAnalysisForm from './pages/ResidenceAnalysis/ResidenceAnalysisForm';
import NIF from './pages/NIF';
import NifLinkRequired from './pages/NIF/NifLinkRequired';
import ContratoAssinaturaForm from './pages/ContratoAssinatura/ContratoAssinaturaForm';
import ContratoFormFill from './pages/Contrato/ContratoFormFill';
import OrcamentoView from './pages/Orcamento/OrcamentoView';
import AgendamentoPublic from './pages/Agendamento/AgendamentoPublic';
import DocumentChecklistFill from './pages/DocumentChecklist/DocumentChecklistFill';
import AdminDocumentos from './pages/Admin/AdminDocumentos';
import AgendamentoGerenciar from './pages/Agendamento/AgendamentoGerenciar';
import PasswordReset from './pages/Admin/PasswordReset';
import NotificationDemo from './pages/NotificationDemo';
import PostView from './pages/PostView';
import FAQ from './pages/FAQ';
import Testimonials from './pages/Testimonials';
import Privacidade from './pages/Privacidade';
import Termos from './pages/Termos';
import LandingPageView from './pages/LandingPageView';
import NurtureUnsubscribe from './pages/NurtureUnsubscribe';
import GroupJoin from './pages/GroupJoin';
import CommunityOffer from './pages/CommunityOffer';
import CommunityJoin from './pages/CommunityJoin';
import AdminCommunities from './pages/Admin/Communities';
import AvatarPageView from './pages/AvatarPageView';
import AdminFAQ from './pages/Admin/FAQ';
import AdminTestimonials from './pages/Admin/Testimonials';
import ColorThemeDemo from './components/ColorThemeDemo';
import ThemeColorDemo from './components/ThemeColorDemo';
import GradientTest from './components/GradientTest';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import ChatWidget from './components/ChatWidget';
import GoogleAnalytics from './components/GoogleAnalytics';
import MetaPixel from './components/MetaPixel';
import CookieConsentBanner from './components/CookieConsentBanner';

function AppLayout() {
  const location = useLocation();
  const { blocked } = useBilling();
  const { isAdmin } = useAuth();
  const isAdminContext = isAdmin && location.pathname.startsWith('/admin');
  const hideSiteChrome =
    location.pathname.startsWith('/analise-visto/') ||
    location.pathname.startsWith('/analise-residencia/') ||
    location.pathname.startsWith('/nif') ||
    location.pathname.startsWith('/assinar-contrato/') ||
    location.pathname.startsWith('/contrato/') ||
    location.pathname.startsWith('/orcamento/') ||
    location.pathname.startsWith('/agendar/') ||
    location.pathname.startsWith('/documentos/') ||
    location.pathname.startsWith('/l/') ||
    location.pathname.startsWith('/a/');

  // Bloqueio por billing afeta só a área administrativa — o site institucional
  // público continua acessível independentemente do status de pagamento da licença.
  if (blocked && location.pathname.startsWith('/admin')) {
    return <BillingBlocked />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GoogleAnalytics />
      <MetaPixel />
      <CookieConsentBanner />
      {!hideSiteChrome && <Navbar />}
      <main className={!hideSiteChrome ? 'pb-[calc(4rem+env(safe-area-inset-bottom))] lg:pb-0' : ''}>
        <div className={isAdminContext ? 'lg:flex lg:items-start' : undefined}>
        {isAdminContext && <AdminSidebar />}
        <div className={isAdminContext ? 'flex-1 min-w-0' : undefined}>
        <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/sobre" element={<About />} />
              <Route path="/imigracao" element={<Immigration />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/post/:id" element={<PostView />} />
                  <Route path="/contato" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/depoimentos" element={<Testimonials />} />
                  <Route path="/privacidade" element={<Privacidade />} />
                  <Route path="/termos" element={<Termos />} />
                  <Route path="/l/:slug" element={<LandingPageView />} />
                  <Route path="/a/:slug" element={<AvatarPageView />} />
                  <Route path="/nutricao/sair/:token" element={<NurtureUnsubscribe />} />
                  <Route path="/grupo/:token" element={<GroupJoin />} />
                  <Route path="/comunidade/acesso/:token" element={<CommunityJoin />} />
                  <Route path="/comunidade/:slug" element={<CommunityOffer />} />
                  <Route path="/nif" element={<NifLinkRequired />} />
                  <Route path="/nif/:token" element={<NIF />} />
                  <Route path="/analise-visto/:token" element={<VisaAnalysisForm />} />
                  <Route path="/analise-residencia/:token" element={<ResidenceAnalysisForm />} />
                  <Route path="/assinar-contrato/:token" element={<ContratoAssinaturaForm />} />
                  <Route path="/contrato/:token" element={<ContratoFormFill />} />
                  <Route path="/orcamento/:token" element={<OrcamentoView />} />
                  <Route path="/agendar/reserva/:manageToken" element={<AgendamentoGerenciar />} />
                  <Route path="/agendar/:token" element={<AgendamentoPublic />} />
                  <Route path="/documentos/:token" element={<DocumentChecklistFill />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/notifications" element={<NotificationDemo />} />
              <Route path="/cores" element={<ColorThemeDemo />} />
              <Route path="/tema" element={<ThemeColorDemo />} />
              <Route path="/gradientes" element={<GradientTest />} />
              
              {/* Admin Routes */}
              <Route path="/admin" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/posts" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminPosts />
                </ProtectedRoute>
              } />
              <Route path="/admin/posts/new" element={
                <ProtectedRoute requiredRole="admin">
                  <PostForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/posts/:id/edit" element={
                <ProtectedRoute requiredRole="admin">
                  <PostForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/contacts" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminContacts />
                </ProtectedRoute>
              } />
              <Route path="/admin/newsletter" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminNewsletter />
                </ProtectedRoute>
              } />
              <Route path="/admin/nif" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminNIF />
                </ProtectedRoute>
              } />
              <Route path="/admin/analise-visto" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminVisaAnalysis />
                </ProtectedRoute>
              } />
              <Route path="/admin/analise-residencia" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminResidenceAnalysis />
                </ProtectedRoute>
              } />
              <Route path="/admin/advogado" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminAdvogado />
                </ProtectedRoute>
              } />
              <Route path="/admin/contrato" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminContrato />
                </ProtectedRoute>
              } />
              <Route path="/admin/orcamento" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminOrcamento />
                </ProtectedRoute>
              } />
              <Route path="/admin/servicos" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminServico />
                </ProtectedRoute>
              } />
              <Route path="/admin/equipe" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminEquipe />
                </ProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminUsers />
                </ProtectedRoute>
              } />
              <Route path="/admin/invites" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminInvites />
                </ProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminSettings />
                </ProtectedRoute>
              } />
              <Route path="/admin/faq" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminFAQ />
                </ProtectedRoute>
              } />
              <Route path="/admin/testimonials" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminTestimonials />
                </ProtectedRoute>
              } />
              <Route path="/admin/redes-sociais" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminRedesSociais />
                </ProtectedRoute>
              } />
              <Route path="/admin/redes-sociais/new" element={
                <ProtectedRoute requiredRole="admin">
                  <RedesSociaisForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/redes-sociais/:id/edit" element={
                <ProtectedRoute requiredRole="admin">
                  <RedesSociaisForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/landing-pages" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminLandingPages />
                </ProtectedRoute>
              } />
              <Route path="/admin/comunidades" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminCommunities />
                </ProtectedRoute>
              } />
              <Route path="/admin/landing-pages/leads" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminLandingQuizLeads />
                </ProtectedRoute>
              } />
              <Route path="/admin/landing-pages/new" element={
                <ProtectedRoute requiredRole="admin">
                  <LandingPageForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/landing-pages/:id/edit" element={
                <ProtectedRoute requiredRole="admin">
                  <LandingPageForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/avatars" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminAvatars />
                </ProtectedRoute>
              } />
              <Route path="/admin/avatars/new" element={
                <ProtectedRoute requiredRole="admin">
                  <AvatarForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/avatars/:id/edit" element={
                <ProtectedRoute requiredRole="admin">
                  <AvatarForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/documentos" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDocumentos />
                </ProtectedRoute>
              } />
              <Route path="/admin/agendamentos" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminAgendamentos />
                </ProtectedRoute>
              } />
              <Route path="/admin/agendamentos/new" element={
                <ProtectedRoute requiredRole="admin">
                  <AgendamentoForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/agendamentos/:id/edit" element={
                <ProtectedRoute requiredRole="admin">
                  <AgendamentoForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/crm" element={<Navigate to="/admin/crm/clientes" replace />} />
              <Route path="/admin/crm/:tab" element={
                <ProtectedRoute requiredRole="admin">
                  <CRM />
                </ProtectedRoute>
              } />
              <Route path="/admin/clientes" element={<Navigate to="/admin/crm/clientes" replace />} />
              <Route path="/admin/clientes/new" element={
                <ProtectedRoute requiredRole="admin">
                  <ClienteForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/clientes/:id" element={
                <ProtectedRoute requiredRole="admin">
                  <ClienteDetail />
                </ProtectedRoute>
              } />
              <Route path="/admin/clientes/:id/edit" element={
                <ProtectedRoute requiredRole="admin">
                  <ClienteForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/vendas" element={<Navigate to="/admin/crm/vendas" replace />} />
              <Route path="/admin/vendas/new" element={
                <ProtectedRoute requiredRole="admin">
                  <VendaForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/vendas/:id/edit" element={
                <ProtectedRoute requiredRole="admin">
                  <VendaForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/pagamentos" element={<Navigate to="/admin/crm/pagamentos" replace />} />
              <Route path="/admin/password-reset" element={
                <ProtectedRoute requiredRole="super_admin">
                  <PasswordReset />
                </ProtectedRoute>
              } />
        </Routes>
        </div>
        </div>
      </main>
      {!hideSiteChrome && <Footer />}
      {!hideSiteChrome && <ChatWidget />}
      {!hideSiteChrome && <MobileBottomNav />}
    </div>
  );
}

function App() {
  const basename =
    process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '/'
      ? process.env.PUBLIC_URL.replace(/\/$/, '')
      : '/';

  return (
    <AuthProvider>
      <NotificationProvider>
        <ConfirmProvider>
        <SettingsProvider>
          <BillingProvider>
            <CookieConsentProvider>
              <Router basename={basename === '/' ? undefined : basename}>
                <ScrollToTop />
                <AppLayout />
              </Router>
            </CookieConsentProvider>
          </BillingProvider>
        </SettingsProvider>
        </ConfirmProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;